import React, {
    RefObject,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../../map-tool/aoi-platform/constants';
import {
    MotionPoint,
    containsWithInset,
    placePopup,
    planCarPresence,
} from '../../central/delivery-monitor/deliveryMonitorMotion';
import { useCarGlide } from '../../central/delivery-monitor/useCarGlide';
import { CrownIcon, HubPinIcon, VehicleIcon } from './icons';
import { displayName, replaySummary } from './pickupTrackingLogic';
import {
    ALERT_COLOR,
    HUB_COLOR,
    LONG_STAY_COLOR,
    STOP_STATUS_COLOR,
    VEHICLE_THEME,
    VEHICLE_TYPES,
    vehicleThemeOf,
} from './pickupTrackingTheme';
import type { TrackingDriver, TrackingHub } from './pickupTrackingTypes';
import type { FeedView } from './useTrackingFeed';
import { ReplayRequest, extendBounds, useStopReplay } from './useStopReplay';
import styles from './PickupDriverTrackingPage.module.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(mapboxgl as any).accessToken = MAPBOX_ACCESS_TOKEN;

// 原型缩放级别比 Mapbox 大 1（原型 256px 瓦片按 z 缩放，枢纽视野 11.25 ≈ Mapbox 10.25）
const HUB_ZOOM = 10.25;
const HUB_FLY_MS = 1100;
const MIN_ZOOM = 3;
const MAX_ZOOM = 17;
const DEFAULT_CENTER: [number, number] = [-95.7129, 37.0902];
const DEFAULT_ZOOM = 3.5;
const LOCATE_ZOOM = 14;
const LOCATE_MS = 900;
const SEARCH_MIN_ZOOM = 12;
const SEARCH_FLY_MS = 800;
const PAN_INSET = 0.12;
const PAN_MS = 700;
const FIT_PAD = 0.18;
const FIT_MS = 800;
const ZOOM_STEP_MS = 250;
const CAR_WIDTH = 34;
const CAR_Z_BASE = 1500;

export interface MapPanelHandle {
    /** 搜索 / 动态 feed 命中：飞到司机，缩放不小于 SEARCH_MIN_ZOOM */
    flyToDriver: (key: string) => void;
    /** 弹窗「地图定位」 */
    locate: (key: string) => void;
    /** 从列表点开：司机不在视野内才平移 */
    panIfOutside: (key: string) => void;
}

interface CarHost {
    marker: mapboxgl.Marker;
    element: HTMLDivElement;
    /** 闪烁层：在动时由 useCarGlide 挂 carMoving */
    pulser: HTMLDivElement;
    /** 车身旋转层：朝向直接写它，车身 SVG 由 portal 渲染进来 */
    rotor: HTMLDivElement;
    /** 不随车身转的徽标层：皇冠 / 光环 / 告警角标 */
    overlay: HTMLDivElement;
}

interface TrackingMapPanelProps {
    hub: TrackingHub | null;
    /** 当前筛选 / 排序后可见的司机；地图只画这些（与原型一致） */
    drivers: TrackingDriver[];
    /** hub | 业务日 | 指定时刻设定基准；变了之后的首轮，本轮没上报的车先删掉、按新范围的最后已知位置重建 */
    scopeKey: string | null;
    /** 位置查询首次加载 / 换变量中：右上角显示「加载中」（同派送大屏 mapStat） */
    positionsLoading: boolean;
    /** 当天有最后已知位置的司机数；不在加载中且为 0 时地图中央提示暂无上报（同派送大屏） */
    positionCount: number;
    kingKey: string | null;
    selectedKey: string | null;
    positionPollMs: number;
    thresholdMin: number;
    replay: ReplayRequest | null;
    replayDriver: TrackingDriver | null;
    /** 有弹窗时为选中司机 key；回放中为 null */
    popupDriverKey: string | null;
    popupRef: RefObject<HTMLDivElement>;
    /** 弹窗；canLocate = 地图上有这辆车（含灰车），由本组件按 host 判断后传入（同派送大屏） */
    popup: ((canLocate: boolean) => React.ReactNode) | null;
    feed: FeedView[];
    onSelectDriver: (d: TrackingDriver) => void;
    onBlankClick: () => void;
    onExitReplay: () => void;
    onFeedPick: (driverKey: string) => void;
}

const toMotionPoint = (p: { lng: number; lat: number; ts: number; bearing: number | null }): MotionPoint => ({
    lng: p.lng,
    lat: p.lat,
    ts: p.ts,
    bearing: p.bearing,
});

const carZIndex = (selected: boolean, alert: boolean, king: boolean) =>
    CAR_Z_BASE + (selected ? 100000 : alert ? 90000 : king ? 80000 : 0);

export const TrackingMapPanel = forwardRef<MapPanelHandle, TrackingMapPanelProps>(
    (
        {
            hub,
            drivers,
            scopeKey,
            positionsLoading,
            positionCount,
            kingKey,
            selectedKey,
            positionPollMs,
            thresholdMin,
            replay,
            replayDriver,
            popupDriverKey,
            popupRef,
            popup,
            feed,
            onSelectDriver,
            onBlankClick,
            onExitReplay,
            onFeedPick,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const containerRef = useRef<HTMLDivElement>(null);
        // 弹窗 / 站点提示框定位的参照系
        const areaRef = useRef<HTMLDivElement>(null);
        const mapRef = useRef<mapboxgl.Map | null>(null);
        const [map, setMap] = useState<mapboxgl.Map | null>(null);
        const hostsRef = useRef<Map<string, CarHost>>(new Map());
        // 每辆车上次交给滑行的轨迹末点 ts：没前进就不再 place
        const placedTsRef = useRef<Map<string, number>>(new Map());
        // 上次同步小车时的范围；与当前不同 = 切换后的首轮（同派送大屏）
        const appliedScopeRef = useRef<string | null>(null);
        const scopeKeyRef = useRef(scopeKey);
        scopeKeyRef.current = scopeKey;
        const hubHostRef = useRef<{ marker: mapboxgl.Marker; element: HTMLDivElement } | null>(null);
        const appliedHubRef = useRef<string | null>(null);
        const [, forceRerender] = useState(0);
        const glide = useCarGlide(positionPollMs, styles.carMoving);

        const blankClickRef = useRef(onBlankClick);
        blankClickRef.current = onBlankClick;
        const replayActive = replay !== null;
        const replayActiveRef = useRef(replayActive);
        replayActiveRef.current = replayActive;

        useEffect(() => {
            if (!containerRef.current || mapRef.current) return;
            let created: mapboxgl.Map | null = null;
            try {
                created = new mapboxgl.Map({
                    container: containerRef.current,
                    style: 'mapbox://styles/mapbox/dark-v11' as unknown as string,
                    center: DEFAULT_CENTER,
                    zoom: DEFAULT_ZOOM,
                    minZoom: MIN_ZOOM,
                    maxZoom: MAX_ZOOM,
                    attributionControl: false,
                });
                // 点地图空白处取消选中；点小车时 mapbox 同样派发 click，按目标元素排除
                created.on('click', (e) => {
                    const target = e.originalEvent.target as HTMLElement | null;
                    if (target?.closest('.mapboxgl-marker')) return;
                    blankClickRef.current();
                });
                mapRef.current = created;
                setMap(created);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[pickup-driver-tracking] mapbox init failed', err);
            }
            const hosts = hostsRef.current;
            const placedTs = placedTsRef.current;
            return () => {
                glide.dispose();
                hosts.forEach((h) => h.marker.remove());
                hosts.clear();
                placedTs.clear();
                hubHostRef.current?.marker.remove();
                hubHostRef.current = null;
                appliedHubRef.current = null;
                mapRef.current?.remove();
                mapRef.current = null;
                setMap(null);
            };
        }, [glide]);

        // 枢纽 marker + 视野：首次直接跳过去，之后切换才飞（原型 loadHub flyTo 1100ms）；
        // 仓库没维护坐标时不画 marker、不动视野
        useEffect(() => {
            if (!map || !hub) return;
            if (hub.lng === null || hub.lat === null) {
                hubHostRef.current?.marker.remove();
                hubHostRef.current = null;
                forceRerender((n) => n + 1);
                return;
            }
            const center: [number, number] = [hub.lng, hub.lat];
            if (!hubHostRef.current) {
                const element = document.createElement('div');
                element.style.cssText = 'width:56px;height:66px;pointer-events:none;z-index:1200;';
                const marker = new mapboxgl.Marker({ element, anchor: 'bottom' }).setLngLat(center).addTo(map);
                hubHostRef.current = { marker, element };
            } else {
                hubHostRef.current.marker.setLngLat(center);
            }
            if (appliedHubRef.current !== hub.id) {
                const first = appliedHubRef.current === null;
                appliedHubRef.current = hub.id;
                if (first) map.jumpTo({ center, zoom: HUB_ZOOM });
                else map.flyTo({ center, zoom: HUB_ZOOM, duration: HUB_FLY_MS });
            }
            forceRerender((n) => n + 1);
        }, [map, hub]);

        // 同步小车：新司机建 host，已有的沿本轮轨迹滑行；不在可见列表里的摘掉，在列表里但本轮没上报的变灰
        useEffect(() => {
            if (!map) return;
            const hosts = hostsRef.current;
            const placedTs = placedTsRef.current;
            const freshKeys = new Set(drivers.filter((d) => d.position && d.fresh).map((d) => d.key));
            // 切换范围后的首轮：本轮没上报的车先删掉（同派送大屏），下面按新范围的最后已知位置重建，免得留在上一个范围的位置上
            const scopeChanged = appliedScopeRef.current !== scopeKeyRef.current;
            appliedScopeRef.current = scopeKeyRef.current;
            if (scopeChanged) {
                planCarPresence(hosts.keys(), freshKeys, true).removed.forEach((key) => {
                    hosts.get(key)?.marker.remove();
                    hosts.delete(key);
                    glide.remove(key);
                });
            }
            const listedKeys = new Set<string>();
            drivers.forEach((d) => {
                listedKeys.add(d.key);
                // 当天一个点都没有的司机不上地图，只在列表里
                if (!d.position || !d.lastPoint) return;
                let host = hosts.get(d.key);
                let track = d.track;
                if (!host) {
                    const h = vehicleThemeOf(d.vehicleType).height;
                    const element = document.createElement('div');
                    element.style.cssText = `width:${CAR_WIDTH}px;height:${h}px;`;
                    if (replayActiveRef.current) element.style.visibility = 'hidden';
                    // element 的 transform 归 mapbox 定位、rotor 的归转向，闪烁动画单独占一层
                    const pulser = document.createElement('div');
                    pulser.style.cssText = `width:${CAR_WIDTH}px;height:${h}px;position:relative;`;
                    const halo = document.createElement('div');
                    halo.className = styles.carHalo;
                    pulser.appendChild(halo);
                    const rotor = document.createElement('div');
                    rotor.style.cssText = `width:${CAR_WIDTH}px;height:${h}px;position:relative;transition:transform 1.2s ease;`;
                    pulser.appendChild(rotor);
                    const overlay = document.createElement('div');
                    overlay.className = styles.carOverlay;
                    pulser.appendChild(overlay);
                    element.appendChild(pulser);
                    const marker = new mapboxgl.Marker({ element, anchor: 'center' })
                        .setLngLat([d.position.lng, d.position.lat])
                        .addTo(map);
                    host = { marker, element, pulser, rotor, overlay };
                    hosts.set(d.key, host);
                    // 首次出现且本轮没有轨迹：直接落在当天最后已知位置、不闪；带真实时间，之后比它新的点还能接着滑
                    if (track.length === 0) {
                        track = [d.lastPoint];
                    } else if (placedTs.has(d.key)) {
                        // 被筛掉后重新显示：直接落在最新点，不从头重放整段轨迹
                        track = [track[track.length - 1]];
                    }
                } else if (track.length > 0 && placedTs.get(d.key) === track[track.length - 1].ts) {
                    // 轨迹没前进（快照轮询等重建了 drivers）：不再 place，免得剩余路径被重新拉满一个周期、小车越走越慢
                    track = [];
                }
                host.pulser.style.setProperty('--car-color', vehicleThemeOf(d.vehicleType).color);
                // 之前变灰的车本轮又上报了：恢复亮色，从当前位置接着滑
                if (d.fresh) host.pulser.classList.remove(styles.carLost);
                // 没有新轨迹点（停着 / 长时间未操作）就不动，也就不会闪烁
                if (track.length > 0) {
                    glide.place(d.key, host, track.map(toMotionPoint));
                    placedTs.set(d.key, track[track.length - 1].ts);
                }
            });
            // 被筛掉 / 快照里没了的车直接摘掉（派送地图不筛选，没有这一步）
            Array.from(hosts.keys()).forEach((key) => {
                if (listedKeys.has(key)) return;
                hosts.get(key)?.marker.remove();
                hosts.delete(key);
                glide.remove(key);
            });
            // 列表里有、本轮没上报的车（含首屏整天查到的）：同派送大屏，变灰留在最后位置（滑到一半的先走完）
            planCarPresence(hosts.keys(), freshKeys, false).lost.forEach((key) =>
                hosts.get(key)?.pulser.classList.add(styles.carLost),
            );
            forceRerender((n) => n + 1);
        }, [map, drivers, glide]);

        // 回放期间所有实时小车隐藏（原型：回放时不画 pins）
        useEffect(() => {
            hostsRef.current.forEach((h) => {
                h.element.style.visibility = replayActive ? 'hidden' : '';
            });
        }, [replayActive]);

        // 层叠：选中 > 告警 > 皇冠
        useEffect(() => {
            drivers.forEach((d) => {
                const host = hostsRef.current.get(d.key);
                if (host) {
                    host.element.style.zIndex = String(
                        carZIndex(selectedKey === d.key, d.anomalies.length > 0, kingKey === d.key),
                    );
                }
            });
        });

        // 弹窗贴车并随车移动：每帧按小车当前像素位置重算
        useEffect(() => {
            if (popupDriverKey === null || !map) return;
            let raf = 0;
            let last = '';
            const loop = () => {
                const el = popupRef.current;
                const area = areaRef.current;
                if (el && area) {
                    const host = hostsRef.current.get(popupDriverKey);
                    const anchor = host ? map.project(host.marker.getLngLat()) : null;
                    const { x, y } = placePopup({
                        anchor: anchor ? { x: anchor.x, y: anchor.y } : null,
                        width: el.offsetWidth,
                        height: el.offsetHeight,
                        boundsWidth: area.clientWidth,
                        boundsHeight: area.clientHeight,
                    });
                    const key = `${x},${y}`;
                    if (key !== last) {
                        el.style.left = `${x}px`;
                        el.style.top = `${y}px`;
                        el.style.visibility = 'visible';
                        last = key;
                    }
                }
                raf = requestAnimationFrame(loop);
            };
            raf = requestAnimationFrame(loop);
            return () => cancelAnimationFrame(raf);
        }, [map, popupDriverKey, popupRef]);

        useImperativeHandle(
            ref,
            () => ({
                flyToDriver: (key) => {
                    const m = mapRef.current;
                    const host = hostsRef.current.get(key);
                    if (!m || !host) return;
                    m.flyTo({
                        center: host.marker.getLngLat(),
                        zoom: Math.max(m.getZoom(), SEARCH_MIN_ZOOM),
                        duration: SEARCH_FLY_MS,
                    });
                },
                locate: (key) => {
                    const host = hostsRef.current.get(key);
                    if (!host) return;
                    mapRef.current?.flyTo({ center: host.marker.getLngLat(), zoom: LOCATE_ZOOM, duration: LOCATE_MS });
                },
                panIfOutside: (key) => {
                    const m = mapRef.current;
                    const host = hostsRef.current.get(key);
                    const b = m?.getBounds();
                    if (!m || !host || !b) return;
                    const { lng, lat } = host.marker.getLngLat();
                    const view = { west: b.getWest(), east: b.getEast(), south: b.getSouth(), north: b.getNorth() };
                    if (!containsWithInset(view, lng, lat, PAN_INSET)) {
                        m.flyTo({ center: [lng, lat], zoom: m.getZoom(), duration: PAN_MS });
                    }
                },
            }),
            [],
        );

        const zoomBy = useCallback((dz: number) => {
            const m = mapRef.current;
            if (!m) return;
            m.zoomTo(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, m.getZoom() + dz)), { duration: ZOOM_STEP_MS });
        }, []);

        // 归位：框住地图上全部小车的当前显示位置（含灰车）+ 枢纽，没有小车不动（同派送大屏）；回放中先退出
        const fitAll = useCallback(() => {
            if (replayActiveRef.current) onExitReplay();
            const m = mapRef.current;
            const pts: { lng: number; lat: number }[] = Array.from(hostsRef.current.values(), (h) =>
                h.marker.getLngLat(),
            );
            if (!m || pts.length === 0) return;
            if (hub && hub.lng !== null && hub.lat !== null) pts.push({ lng: hub.lng, lat: hub.lat });
            m.fitBounds(extendBounds(pts, FIT_PAD), { padding: 0, maxZoom: MAX_ZOOM, duration: FIT_MS });
        }, [hub, onExitReplay]);

        const { layer: replayLayer, tooltip: replayTooltip } = useStopReplay({
            map,
            areaRef,
            replay,
            driver: replayDriver,
            thresholdMin,
        });

        // 不 memo：host 在 effect 里才创建，effect 跑完 forceRerender 才能写进 portal（同派送大屏 marker 的做法）
        const hubNode =
            hub && hubHostRef.current
                ? createPortal(
                      <div className={styles.hubIcon} title={hub.code}>
                          <span className={styles.hubLabel}>{`HUB · ${hub.code}`}</span>
                          <HubPinIcon />
                          <i className={styles.hubRing} />
                      </div>,
                      hubHostRef.current.element,
                      'hub',
                  )
                : null;

        const carNodes: React.ReactNode[] = [];
        drivers.forEach((d) => {
            const host = hostsRef.current.get(d.key);
            if (!host) return;
            const selected = selectedKey === d.key;
            const king = kingKey === d.key;
            const alert = d.anomalies.length > 0;
            const label = [
                displayName(d),
                d.vehicleType ? t(`pickup.driverTracking.vehicle.${d.vehicleType}`) : '',
                alert ? d.anomalies.map((a) => t(`pickup.driverTracking.anomaly.${a}`)).join(' / ') : '',
            ]
                .filter(Boolean)
                .join(' · ');
            carNodes.push(
                createPortal(
                    <button
                        type="button"
                        className={`${styles.carWrap} ${king ? styles.carKing : ''}`}
                        style={{
                            height: vehicleThemeOf(d.vehicleType).height,
                            padding: 0,
                            border: 0,
                            background: 'none',
                            // 非皇冠车按车型色补一层轻发光；皇冠走 kfKingGlow 动画自带 filter，别在这里覆盖
                            filter: king ? undefined : `drop-shadow(0 0 4px ${vehicleThemeOf(d.vehicleType).color}80)`,
                        }}
                        aria-label={label}
                        title={label}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelectDriver(d);
                        }}
                    >
                        <VehicleIcon type={d.vehicleType} color={vehicleThemeOf(d.vehicleType).color} selected={selected} />
                    </button>,
                    host.rotor,
                    `car-${d.key}`,
                ),
                createPortal(
                    <>
                        {king && <i className={styles.kingring} />}
                        {king && (
                            <span className={styles.crown}>
                                <CrownIcon />
                            </span>
                        )}
                        {alert && (
                            <>
                                <i className={styles.alertring} />
                                <span className={styles.abadge}>!</span>
                            </>
                        )}
                        {selected && <i className={styles.selring} />}
                    </>,
                    host.overlay,
                    `ov-${d.key}`,
                ),
            );
        });

        const legend = replayDriver
            ? [
                  { c: STOP_STATUS_COLOR.ok, label: t('pickup.driverTracking.status.ok') },
                  { c: STOP_STATUS_COLOR.fail, label: t('pickup.driverTracking.status.fail') },
                  { c: STOP_STATUS_COLOR.wait, label: t('pickup.driverTracking.status.wait') },
                  { c: LONG_STAY_COLOR, label: t('pickup.driverTracking.sumLong', { min: thresholdMin }) },
              ]
            : [
                  ...VEHICLE_TYPES.map((k) => ({
                      c: VEHICLE_THEME[k].color,
                      label: t(`pickup.driverTracking.vehicle.${k}`),
                  })),
                  { c: ALERT_COLOR, label: t('pickup.driverTracking.alertTitle') },
                  { c: HUB_COLOR, label: 'HUB' },
              ];

        const sum = replayDriver ? replaySummary(replayDriver, thresholdMin) : null;

        return (
            <div className={`${styles.mapwrap} ${styles.panel}`}>
                <div className={styles.phead}>
                    <span className={styles.dia} />
                    <h2 className={styles.pheadTitle}>{t('pickup.driverTracking.mapTitle')}</h2>
                    <span className={styles.pheadHint}>{t('pickup.driverTracking.hint')}</span>
                </div>
                <div className={styles.mapArea} ref={areaRef}>
                    <div className={styles.mapBox} ref={containerRef} />
                    {!positionsLoading && positionCount === 0 && (
                        <div className={styles.mapEmpty}>{t('pickup.driverTracking.noPosition')}</div>
                    )}
                    {hubNode}
                    {carNodes}
                    {replayLayer}
                    {replayTooltip}
                    {popup && popupDriverKey !== null ? popup(hostsRef.current.has(popupDriverKey)) : null}
                </div>
                {replayDriver && sum && (
                    <div className={styles.trackbar}>
                        <span
                            className={styles.trackDot}
                            style={{ background: vehicleThemeOf(replayDriver.vehicleType).color }}
                        />
                        <b style={{ color: '#fff', fontWeight: 700 }}>
                            {displayName(replayDriver)}
                            {replayDriver.vehicleType && ` · ${t(`pickup.driverTracking.vehicle.${replayDriver.vehicleType}`)}`}
                        </b>
                        <span style={{ color: '#7fb6dd' }}>{t('pickup.driverTracking.tracking')}</span>
                        <span style={{ color: '#a9d5f0' }}>
                            {t('pickup.driverTracking.sumOk')} {sum.ok} · {t('pickup.driverTracking.sumFail')} {sum.fail} ·{' '}
                            {t('pickup.driverTracking.sumWait')} {sum.wait} ·{' '}
                            {t('pickup.driverTracking.sumLong', { min: thresholdMin })} {sum.long}
                        </span>
                        <button type="button" className={styles.tbtn} onClick={onExitReplay}>
                            {t('pickup.driverTracking.exitTrack')}
                        </button>
                    </div>
                )}
                <div className={styles.legend}>
                    {legend.map((g) => (
                        <span key={g.label}>
                            <i style={{ background: g.c }} />
                            {g.label}
                        </span>
                    ))}
                </div>
                <div className={styles.mapStat}>
                    {positionsLoading
                        ? t('pickup.driverTracking.mapLoading')
                        : t('pickup.driverTracking.stat', { sec: Math.round(positionPollMs / 1000) })}
                </div>
                <div className={styles.zoomBar}>
                    <button
                        type="button"
                        className={styles.mbtn}
                        onClick={() => zoomBy(1)}
                        aria-label={t('pickup.driverTracking.zoomIn') || ''}
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className={styles.mbtn}
                        onClick={() => zoomBy(-1)}
                        aria-label={t('pickup.driverTracking.zoomOut') || ''}
                    >
                        −
                    </button>
                </div>
                <button
                    type="button"
                    className={`${styles.mbtn} ${styles.fitBtn}`}
                    onClick={fitAll}
                    title={t('pickup.driverTracking.fit') || ''}
                    aria-label={t('pickup.driverTracking.fit') || ''}
                >
                    ⌖
                </button>
                <div className={styles.feed}>
                    {feed.map((f) => (
                        <button
                            key={f.id}
                            type="button"
                            className={`${styles.fe} ${f.fail ? styles.feFail : ''} ${f.out ? styles.feOut : ''} ${f.typed ? styles.feTyped : ''}`}
                            title={t('pickup.driverTracking.feedTip') || ''}
                            onClick={(e) => {
                                e.stopPropagation();
                                onFeedPick(f.driverKey);
                            }}
                        >
                            <i />
                            <span className={styles.feTxt}>
                                <b className={styles.feName}>{f.driverName}</b> <span>{f.body.slice(0, f.typedLen)}</span>
                            </span>
                            <span className={styles.feSite}>{f.siteName}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    },
);

TrackingMapPanel.displayName = 'TrackingMapPanel';
